import * as React from 'react';
import { useEffect } from 'react';
import { ITopBarFilterButtonProps } from './types';
import { Button, Icon, Input } from 'kui';
import './_TopBarFilterButton.scss';
import {
    TOP_BAR_FILTER_SECTIONS,
    TOPBAR_FILTER_FOCUSED_PLACEHOLDER,
    TOPBAR_FILTER_MOBILE,
    TOPBAR_FILTER_PLACEHOLDER,
    MEDIA_DEFAULT,
    TOPBAR_FILTER_RECENT_EMPTY_PLACEHOLDER,
    TOPBAR_FILTER_HEADER,
    SEARCH_LIMIT,
    SMALL_SCREEN_SIZE
} from './constants';
import { root } from '../../../../../../store/constants';
import { SearchedCard } from './SearchedCard/SearchedCard';
import { SearchSelect } from '../../../../base/components/SearchSelect/SearchSelect';
import { ISearchSelectOption } from '../../../../base/components/SearchSelect/types';
import { filterArrayByValue } from '../../../../helpers/search/filterArrayByValue';
import { AnalyzeRender } from '../../../../helpers/memoizeHelper';
import { GOOGLE_SPACING } from '../../../../../../const';
import { NAVIGATION_PANEL_INNER_CLASS } from '../../../navigationPanel/constants';
// @ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`TopBarFilterButton`) : null;

export function TopBarFilterButton ({
    keyword,
    cards,
    recentCards,
    isActive,
    asidePanelWidth,
    isRelatedCardsPanel,
    isShown,
    afterSearchCard,
    afterSearchFilter,
    onFilterChange,
    onClickButton,
    onBlur,
    onSelect,
    onClickInput,
}: ITopBarFilterButtonProps) {
    if (analyzeRender) analyzeRender.call(`TopBarFilterButton`);
    const className = 'top-bar__filter';
    const classNameActive = isActive ? className +'--active' : '';
    const classNameInput = 'top-bar__filter-input';
    const classNameSearch = 'top-bar__filter-search';
    const classNameOk = 'top-bar__filter-ok';
    const classNameIcon = 'top-bar__filter-icon';
    const classNameButtonMobile = 'top-bar__filter-button-mobile';
    const divRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const debounce = React.useRef(null);
    let [isSearchFocused, _setIsSearchFocused] = React.useState(isActive && !!keyword);
    const classNameFocused = isSearchFocused ? className + '--focused' : '';
    let [searchValue, _setSearchValue] = React.useState('');
    let [inputValue, _setInputValue] = React.useState(keyword);
    const [cardsHook, setCardsHook] = React.useState(cards);
    const [isFocus, setFocus] = React.useState(null);

    function setSearchValue (val: string) {
        searchValue = val;
        _setSearchValue(val);
    };

    function setInputValue (val: string) {
        inputValue = val;
        _setInputValue(val);
    };

    function setIsSearchFocused (isSearchFocusedNew: boolean) {
        isSearchFocused = isSearchFocusedNew;
        _setIsSearchFocused(isSearchFocusedNew);
    };

    function closeSearch () {
        setIsSearchFocused(false);
        const input = divRef.current.querySelector('input'); // в handleSearchSelect blur почему-то срабатывает только на канбанВью
        if (input && input === document.activeElement) input.blur();
    };

    function applyFilterCloseSearch () {
        if (searchValue) {
            onFilterChange(searchValue);
            afterSearchFilter();
        }
        closeSearch();
    };

    function handleSearchChange (val: string) {
        setSearchValue(val);
        if (val) {
            if (isRelatedCardsPanel) {
                if (debounce.current) clearTimeout(debounce.current);
                debounce.current = setTimeout(() => {
                    applyFilterCloseSearch()
                    requestAnimationFrame(()=>{
                        const input = divRef.current.querySelector('input');
                        if (input && input !== document.activeElement) input.focus();
                    })
                }, 500)
            }
        } else {
            if (debounce.current) clearTimeout(debounce.current);
        }
    };

    function handleSearchKeyDown (event: React.KeyboardEvent) {
        const enterKey = event.keyCode === 13;
        const escKey = event.keyCode === 27;
        if (enterKey) {
            setTimeout(applyFilterCloseSearch, 100); // подождать handleSearchSelect
        } else if (escKey) {
            closeSearch();
        }
    }

    function handleSearchOpen () {
        setIsSearchFocused(true);
        setCardsHook(searchValue ? cards : recentCards); // обновлять карточки для кейса между переключением досок
        onClickInput();
    };

    function handleSearchSelect (value: string) {
        onSelect(value, !searchValue);
        closeSearch();
        afterSearchCard();
    };

    function handleInputChange (e: React.SyntheticEvent) {
        const { value } = e.target as HTMLInputElement;
        if (debounce.current) clearTimeout(debounce.current);
        debounce.current = setTimeout(
            () => onFilterChange(value),
            500
        );
    };

    function handleMobileButtonClick (e: React.SyntheticEvent) {
        if (asidePanelWidth) { // KNB-4014 иногда не успевают сфокусироваться
            setTimeout(() => {
                const input = divRef.current.querySelector('input') as HTMLElement;
                if (input) {
                    setFocus(true);
                    input.focus();
                    input.click();
                }
            }, 0)
        } else {
            onClickButton();
        }
    };

    const [isFullSize, setFullSize] = React.useState(root._innerWidth > MEDIA_DEFAULT);
    useEffect(() => {
        const onResize = () => setFullSize(root._innerWidth > MEDIA_DEFAULT);
        onResize();
        root.addEventListener('resize', onResize);
        return () => root.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!isSearchFocused) setCardsHook(searchValue ? cards : recentCards); // не обновлять карты, пока поиск в фокусе
    }, [cards]);

    useEffect(() => {
        setCardsHook(!searchValue ? recentCards : cards);
    }, [searchValue]);

    useEffect(() => {
        setInputValue(keyword);
        setSearchValue('');
    }, [isActive, keyword]);

    const changedRef = React.useRef(null);
    useEffect(() => { // KNB-2024 focus on clear click
        if (keyword && !changedRef.current) {
            changedRef.current = true;
        } else if (!keyword && changedRef.current) {
            changedRef.current = false;
            const input = document.querySelector(`.${classNameSearch} .kui-input__item`) as HTMLElement;
            if (input) input.focus();
        }
    }, [keyword]);

    useEffect(() => {
        function handleShortcut (event: any) {
            const e = event as React.KeyboardEvent;
            if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'а')) {
                e.preventDefault();
                const input = divRef.current.querySelector('.kui-input');
                if (input) input.click();
            }
        };
        root.addEventListener('keydown', handleShortcut);

        return (() => {
            root.removeEventListener('keydown', handleShortcut);
            if (debounce.current) clearTimeout(debounce.current);
        });
    }, []);

    const [isSmallScreen ,setSmallScreen] = React.useState(null);
    React.useEffect(() => {
        if (!asidePanelWidth || isRelatedCardsPanel) {
            setSmallScreen(false);
            return;
        }
        const onResize = () => {
            const asideLeft = asidePanelWidth + GOOGLE_SPACING * 2;
            let navPanelRight = 0;
            const navPanel = document.querySelector('.' + NAVIGATION_PANEL_INNER_CLASS) as HTMLElement;
            if (navPanel) {
                navPanelRight = navPanel.offsetWidth + GOOGLE_SPACING * 2;
            }
            setSmallScreen(root._innerWidth - asideLeft - navPanelRight < SMALL_SCREEN_SIZE);
        };
        onResize();
        root.addEventListener('resize', onResize);
        return () => root.removeEventListener('resize', onResize);
    }, [asidePanelWidth, isRelatedCardsPanel]);

    const onFilterOptions = (
        list: ISearchSelectOption[],
        searchQuery: string
    ): ISearchSelectOption[] => {
        const result: ISearchSelectOption[] = [];
        const filteredValues: string[] = [];
        TOP_BAR_FILTER_SECTIONS.forEach((section, index) => {
            let sectionOptions: ISearchSelectOption[] = filterArrayByValue(list, section.field, searchQuery);
            if (sectionOptions.length) {
                filteredValues.push(...sectionOptions.map(option => option.value));
                sectionOptions.sort((x, y) => y.lastActivity - x.lastActivity)
                if (sectionOptions.length) {
                    result.push({
                        text: section.name,
                        value: section.field,
                        options: sectionOptions
                    })
                }
            }
        });
        return result;
    }

    return isShown && (
        <div
            className={`
                ${className}
                ${classNameActive}
                ${classNameFocused}
                ${isSmallScreen ? className + '--compact' : ''}
            `}
            ref={divRef}
        >
            {isActive
                ? <Input
                    autosize={false}
                    className={classNameInput}
                    isClearable
                    ref={inputRef}
                    searchPlaceholder={TOPBAR_FILTER_PLACEHOLDER}
                    value={inputValue}
                    variant={'search'}
                    onBlur={onBlur}
                    onChange={handleInputChange}
                    onClick={onClickInput}
                />
                : <SearchSelect
                    className={`${classNameSearch} ${isFullSize && !searchValue ? classNameSearch + '--empty' : ''}`}
                    isClearable
                    isCloseOnEnter={false}
                    isClearOnOpen={false}
                    isFitWindow
                    isEnterEnabled={true}
                    isAcronymSearchEnabled={false}
                    option={SearchedCard}
                    options={cardsHook}
                    isMountDropdown={!isRelatedCardsPanel}
                    header={!searchValue && !isRelatedCardsPanel && recentCards.length
                        ? <span className={classNameSearch + '-header'}>{TOPBAR_FILTER_HEADER}</span>
                        : null}
                    isShowMore={!searchValue && !isRelatedCardsPanel && !!recentCards.length}
                    ref={inputRef}
                    searchDebounce={500}
                    searchPlaceholder={isFullSize && isFocus ? TOPBAR_FILTER_FOCUSED_PLACEHOLDER : TOPBAR_FILTER_PLACEHOLDER}
                    text={searchValue}
                    emptyPlaceholder={!searchValue && !isRelatedCardsPanel ? TOPBAR_FILTER_RECENT_EMPTY_PLACEHOLDER : null}
                    onBlur={() => setFocus(false)}
                    onInputChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => setFocus(true)}
                    onOpened={handleSearchOpen}
                    onSelect={handleSearchSelect}
                    onFilterOptions={searchValue ? onFilterOptions : null}
                />
            }
            <div
                className={classNameOk}
                // onClick={} // можно повесить segment. Пока ничего не надо, т.к. работает blur
            />
            <Button
                className={`${classNameButtonMobile} ${!asidePanelWidth ? 'handle-click-ignore' : ''}`}
                variant={'fab'}
                color={'white'}
                tooltip={{
                    value: TOPBAR_FILTER_MOBILE,
                    isNoWrap: true,
                    direction: 'down'
                }}
                onClick={handleMobileButtonClick}
            >
                <Icon className={classNameIcon} size={24} xlink={'search'}/>
            </Button>
        </div>
    );
};
